import React from 'react'
import { useTranslation } from 'react-i18next'
import { FieldHookConfig, useField, useFormikContext } from 'formik'
import { VinTextField as UIVinTextField } from '@carsys/ui'
import { isInvalid } from './utils'
import { useRegisterFormField } from './TabbedForm/FormTabPanel'
import { IconButton, InputAdornment, Tooltip } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'

interface OwnProps {
  maxLength?: number
  vehicleUUID?: string
  vinMasked?: boolean
  onShowVinClick?: (registration: string) => Promise<string | null>
  isVinMasked?: (vin: string) => boolean
  registration?: string
}

interface TextFieldProps<Value>
  extends Omit<React.ComponentProps<typeof UIVinTextField>, 'component' | 'defaultChecked' | 'defaultValue' | 'name' | 'value'>,
    Pick<FieldHookConfig<Value>, 'name' | 'validate'>,
    OwnProps {}

export function VinTextField<Value>({
  name,
  validate,
  maxLength,
  vehicleUUID,
  vinMasked,
  onShowVinClick,
  isVinMasked: isVinMaskedProp,
  registration,
  ...props
}: TextFieldProps<Value>) {
  const { t, i18n } = useTranslation()
  const [input, meta] = useField<Value>({ name, validate, maxLength })
  const { setFieldValue } = useFormikContext<any>()
  useRegisterFormField(name)

  const metaError = i18n.exists(meta.error!) ? t(meta.error!) : meta.error
  const propsHelperText = i18n.exists(String(props.helperText!)) ? t(String(props.helperText!)) : props.helperText

  const defaultIsVinMasked = (vin: string): boolean => {
    if (!vin || typeof vin !== 'string') return false
    return vin.startsWith('******')
  }

  const isVinMasked = isVinMaskedProp || defaultIsVinMasked
  const isMasked = vinMasked ?? isVinMasked(String(input.value || ''))

  const handleShowVinClick = async () => {
    if (registration && onShowVinClick) {
      try {
        const unmaskedVin = await onShowVinClick(registration)
        if (unmaskedVin) {
          setFieldValue(name, unmaskedVin)
        }
      } catch (error) {
        console.error('Failed to unmask VIN:', error)
      }
    }
  }

  return (
    <UIVinTextField
      id={input.name}
      error={isInvalid(meta)}
      {...props}
      {...input}
      helperText={isInvalid(meta) ? metaError : propsHelperText}
      InputProps={{
        ...props.InputProps,
        endAdornment: isMasked ? (
          <InputAdornment position='end'>
            <Tooltip title={t('vehicle.show_full_vin')}>
              <IconButton size='small' onClick={handleShowVinClick} edge='end'>
                <VisibilityIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ) : (
          props.InputProps?.endAdornment
        )
      }}
    />
  )
}
